import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchIbamaJudges } from '@/services/ibama';
import { officeKindLabel } from '@/pages/ibama/judgeLabels';

/**
 * The named judging individuals IBAMA's two substrates record.
 *
 * Two tracks sit side by side and are never pooled: CONAMA CER relators
 * identified in the collegiate minutes (historical), and holders of IBAMA's
 * judging offices evidenced by DOU acts and gov.br occupancy pages. The
 * office column counts judgments DATED INSIDE A TERM WINDOW the published
 * act defines - an incumbency attribution, never a signature. There is no
 * strictness measure anywhere on this page.
 */
export function IbamaJudgesPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'judges'],
    queryFn: fetchIbamaJudges,
  });
  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }



  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:judges.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:judges.subtitle')}</p>
        <p className="text-xs">
          <Link
            to="/ibama/judges/methodology"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t('ibama:judges.methodologyLink')}
          </Link>
        </p>
      </header>

      <ExplainerCard i18nKey="ibama:judges.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-bench>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:judges.bench.title')}
        </h2>
        <Answer answer={data.bench} help={t('ibama:judges.bench.help')}>
          {(bench) => (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2" data-coverage>
                <div className="rounded-md border p-3 text-xs text-muted-foreground">
                  <div className="font-medium text-foreground">
                    {t('ibama:judges.tallies.firstTitle')}
                  </div>
                  {t('ibama:judges.tallies.firstBody', {
                    attributed: number.format(bench.tallies.first_attributed),
                    total: number.format(bench.tallies.first_instance_total),
                  })}
                </div>
                <div className="rounded-md border p-3 text-xs text-muted-foreground">
                  <div className="font-medium text-foreground">
                    {t('ibama:judges.tallies.boardTitle')}
                  </div>
                  {t('ibama:judges.tallies.boardBody', {
                    attributed: number.format(bench.tallies.board_attributed),
                    total: number.format(bench.tallies.board_total),
                  })}
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-conama-summary>
                {t('ibama:judges.tallies.conama', {
                  items: number.format(bench.conama_items_judged),
                  people: number.format(bench.conama_people),
                })}{' '}
                <Link to="/ibama/conama/board" className="text-primary underline-offset-2 hover:underline">
                  {t('ibama:judges.tallies.conamaLink')}
                </Link>
              </p>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('ibama:judges.bench.person')}</th>
                      <th className="px-3 py-2 font-medium">{t('ibama:judges.bench.track')}</th>
                      <th className="px-3 py-2 font-medium">{t('ibama:judges.bench.office')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:judges.bench.units')}
                      </th>
                      <th className="px-3 py-2 font-medium">{t('ibama:judges.bench.years')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bench.people.map((p) => (
                      <tr key={`${p.track}-${p.slug}`} className="border-t align-top" data-person={p.slug}>
                        <td className="px-3 py-2 text-foreground">
                          {/* two branches, not one helper: the detail-route scan
                              needs each literal prefix right after `to={` */}
                          {p.track === 'conama' ? (
                            <Link
                              to={`/ibama/conama/r/${p.slug}`}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {p.name}
                            </Link>
                          ) : (
                            <Link
                              to={`/ibama/j/${p.slug}`}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {p.name}
                            </Link>
                          )}
                          {p.team_attributed > 0 ? (
                            <span
                              className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground"
                              title={t('ibama:judges.bench.teamHint')}
                            >
                              {t('ibama:judges.bench.teamBadge')}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {t(`ibama:judges.track.${p.track}`)}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {officeKindLabel(t, p.kind)}
                          {p.uf ? ` (${p.uf})` : ''}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(p.units)}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {p.first_year ?? '—'}–{p.last_year ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bench.suppressed > 0 ? (
                <p className="text-xs text-muted-foreground" data-suppressed>
                  {t('ibama:judges.bench.suppressed', {
                    n: number.format(bench.suppressed),
                    threshold: number.format(bench.threshold_conama),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ibama:judges.guide" />
        </div>
      </section>
    </div>
  );
}
